import React, { useEffect, useRef } from 'react';

// Street / City / State / ZIP with an optional Google Places autocomplete on the street box.
// Autocomplete turns on only when REACT_APP_GOOGLE_MAPS_KEY is set (Places API enabled for that key);
// otherwise these are plain fields. value: { street, city, state, zip }; onChange(patch) also gets
// `propertyAddress`, the combined string that existing screens and records use.
const KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';

export const combineAddress = ({ street, city, state, zip } = {}) =>
  [street, city, [state, zip].filter(Boolean).join(' ')].map((x) => String(x || '').trim()).filter(Boolean).join(', ');

// Best-effort split of an older combined address ("123 Main St, Naples, FL 34108").
export const parseAddress = (s = '') => {
  const parts = String(s).split(',').map((x) => x.trim()).filter(Boolean);
  const out = { street: parts[0] || '', city: '', state: 'FL', zip: '' };
  if (parts.length >= 2) out.city = parts[1];
  const last = parts.length >= 3 ? parts[parts.length - 1] : '';
  const m = last.match(/^([A-Za-z]{2})?\s*(\d{5}(?:-\d{4})?)?$/);
  if (m && (m[1] || m[2])) { out.state = (m[1] || 'FL').toUpperCase(); out.zip = m[2] || ''; }
  else if (last) out.city = [out.city, last].filter(Boolean).join(', ');
  return out;
};

let loader = null;
const loadPlaces = () => {
  if (!KEY) return Promise.reject(new Error('no key'));
  if (window.google?.maps?.places) return Promise.resolve();
  if (!loader) loader = new Promise((resolve, reject) => {
    const sc = document.createElement('script');
    sc.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(KEY)}&libraries=places`;
    sc.async = true; sc.onload = resolve; sc.onerror = reject;
    document.head.appendChild(sc);
  });
  return loader;
};

export default function AddressFields({ value = {}, onChange, inputStyle = {}, labelStyle = {} }) {
  const streetRef = useRef(null);
  const latest = useRef(value);
  latest.current = value;
  const emit = (patch) => { const next = { ...latest.current, ...patch }; onChange({ ...patch, propertyAddress: combineAddress(next) }); };
  const emitRef = useRef(emit);
  emitRef.current = emit;

  useEffect(() => {
    if (!KEY || !streetRef.current) return undefined;
    let ac = null;
    loadPlaces().then(() => {
      ac = new window.google.maps.places.Autocomplete(streetRef.current, { types: ['address'], componentRestrictions: { country: 'us' }, fields: ['address_components'] });
      ac.addListener('place_changed', () => {
        const comps = ac.getPlace()?.address_components || [];
        const get = (t, short) => { const c = comps.find((x) => x.types.includes(t)); return c ? (short ? c.short_name : c.long_name) : ''; };
        emitRef.current({
          street: [get('street_number'), get('route')].filter(Boolean).join(' '),
          city: get('locality') || get('sublocality') || get('postal_town'),
          state: get('administrative_area_level_1', true) || 'FL',
          zip: get('postal_code'),
        });
      });
    }).catch(() => {});
    return () => { if (ac && window.google?.maps?.event) window.google.maps.event.clearInstanceListeners(ac); };
  }, []);

  const f = (k, label, extra = {}) => (
    <label style={{ display: 'block', ...(extra.wrap || {}) }}>
      <span style={labelStyle}>{label}</span>
      <input ref={k === 'street' ? streetRef : undefined} style={inputStyle} value={value[k] || ''} autoComplete={k === 'street' && KEY ? 'off' : undefined}
        placeholder={extra.placeholder} maxLength={extra.maxLength} onChange={(e) => emit({ [k]: k === 'state' ? e.target.value.toUpperCase() : e.target.value })} />
    </label>
  );
  return (
    <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0 16px' }}>
      {f('street', 'Street (number and name)', { wrap: { gridColumn: '1 / -1' }, placeholder: KEY ? 'Start typing the address' : '123 Example Blvd' })}
      {f('city', 'City')}
      {f('state', 'State', { maxLength: 2 })}
      {f('zip', 'ZIP', { maxLength: 10 })}
    </div>
  );
}
